import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Save, AlertTriangle, Library } from "lucide-react";
import { toast } from "sonner";
import { Field, TextInput, TextArea } from "@/components/ReportPrimitives";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";
import useUnsavedChanges from "@/hooks/useUnsavedChanges";
import {
  emptyLibraryRecord, validateLibraryRecord, withDerived,
  CATEGORIES, CATEGORY_LABELS,
  BOP_TYPES, BIT_TYPES, PUMP_TYPES,
  STABILIZER_BLADE_TYPES, RSS_STEERING_MODES, MWD_TELEMETRY_TYPES,
} from "@/lib/reflibs/schema";
import {
  createUserRecord, updateUserRecord, getUserRecord, cloneAsUserRecord,
} from "@/lib/reflibs/storage";
import { findById } from "@/lib/reflibs/lookup";
import {
  getLibraryReturnCtx, restoreDraftState,
} from "@/lib/reflibs/returnContext";

function NativeSelect({ value, onChange, options, testId, disabled }) {
  return (
    <select value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            data-testid={testId}
            className="input-rs">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function NumIn(props) {
  return <TextInput type="number" {...props} />;
}

// Category-aware title — mirrors the per-category identity fields used by
// each Form component below. Tubulars (drill_pipe/hwdp/drill_collar/casing/
// tubing) keep the original nominalSize/grade/connection title; every other
// category previously fell through to that same expression and printed
// "(unnamed) · undefined · undefined" once saved.
function _recordTitle(rec) {
  if (rec.category === "bop") {
    return [rec.stackName || "(unnamed)", rec.nominalSize, rec.workingPressure ? `${rec.workingPressure} psi` : null]
      .filter(Boolean).join(" · ");
  }
  if (rec.category === "bit") {
    return [rec.bitSize ? `${rec.bitSize}"` : "(unnamed)", rec.bitType, rec.model].filter(Boolean).join(" · ");
  }
  if (rec.category === "pump") {
    return [rec.pumpModel || "(unnamed)", rec.manufacturer, rec.pumpType].filter(Boolean).join(" · ");
  }
  if (["mud_motor", "rotary_steerable", "mwd_tool", "lwd_tool", "stabilizer", "crossover"].includes(rec.category)) {
    return [rec.model || "(unnamed)", rec.manufacturer, rec.outerDiameter && `${rec.outerDiameter}"`]
      .filter(Boolean).join(" · ");
  }
  return `${rec.nominalSize || "(unnamed)"} · ${rec.grade} · ${rec.connection}`;
}

export default function ReferenceLibraryRecordEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { category, id } = useParams();
  const decodedId = id ? decodeURIComponent(id) : null;
  const isNew = !decodedId;
  const returnCtx = getLibraryReturnCtx(location);

  const [rec, setRec] = useState(() => emptyLibraryRecord(category || "drill_pipe"));
  const [origin, setOrigin] = useState(null);   // "seed" | "user" | null
  const [notFound, setNotFound] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (isNew) {
      setRec(emptyLibraryRecord(category || "drill_pipe"));
      setOrigin(null);
      setNotFound(false);
      return;
    }
    const userRow = getUserRecord(decodedId);
    if (userRow) {
      setRec(userRow);
      setOrigin("user");
      setNotFound(false);
      return;
    }
    const seedRow = findById(decodedId);
    if (seedRow) {
      setRec(seedRow);
      setOrigin("seed");
      setNotFound(false);
      return;
    }
    setNotFound(true);
  }, [decodedId, isNew, category]);

  const readOnly = origin === "seed";
  const set = (patch) => setRec((cur) => withDerived({ ...cur, ...patch }));

  // RC-1 · Unsaved Changes Protection — same guard used by every other
  // engineering editor, now wired here so the ← BACK action (and tab
  // close / browser back) warns before discarding an unsaved library
  // record. Disabled entirely for read-only seed records (D5 fix): with
  // every field now genuinely disabled (see ReportPrimitives.jsx), `rec`
  // can never legitimately drift from the loaded seed row, but this also
  // stops the guard from ever calling updateUserRecord() on a seed id —
  // which has no corresponding user record and previously surfaced a raw
  // "User record not found" error on Save & Leave.
  const unsaved = useUnsavedChanges({ data: rec, enabled: !notFound && !readOnly, onSave: () => handleSave() });

  const handleSave = () => {
    const baked = withDerived(rec);
    const v = validateLibraryRecord(baked);
    if (!v.ok) {
      setErrors(v.errors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
    try {
      const saved = isNew
        ? createUserRecord(baked)
        : updateUserRecord(decodedId, baked);
      setErrors([]);
      unsaved.markSaved(saved);
      toast.success(isNew ? "Record created" : "Record saved");
      // Reference Library Return-Workflow — Save never forces a return to
      // the originating editor. It stays on this page (now pointing at the
      // saved id) with the ← BACK action still available, so the operator
      // decides when to leave and the origin's draft context is untouched.
      if (isNew) {
        navigate(`/reference-libraries/${saved.category}/${encodeURIComponent(saved.id)}`,
          { replace: true, state: location.state });
      } else {
        setRec(saved);
      }
      return true;
    } catch (e) {
      setErrors(e.validationErrors || [e.message || "Save failed"]);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
  };

  const handleBack = () => {
    if (returnCtx) unsaved.guardedNavigate(returnCtx.from, restoreDraftState(returnCtx));
    else unsaved.guardedNavigate(`/reference-libraries/${rec.category}`);
  };

  const handleCloneSeed = () => {
    const copy = cloneAsUserRecord(rec);
    navigate(`/reference-libraries/${copy.category}/${encodeURIComponent(copy.id)}`, { state: location.state });
  };

  if (notFound) {
    return (
      <div className="p-4 max-w-2xl mx-auto" data-testid="reflibs-not-found">
        <h1 className="font-heading text-2xl mb-2">Record not found</h1>
        <button onClick={() => (returnCtx ? navigate(returnCtx.from, { state: restoreDraftState(returnCtx) }) : navigate("/reference-libraries"))}
                className="text-[var(--rs-amber)]">← Back to Reference Libraries</button>
      </div>
    );
  }

  return (
    <div data-testid="reflibs-editor" className="flex flex-col gap-4 pb-12">
      <header className="flex items-center justify-between gap-3">
        <button type="button"
                onClick={handleBack}
                data-testid="reflibs-header-back"
                className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]">
          <ArrowLeft className="w-3.5 h-3.5" /> {returnCtx ? "Back" : (CATEGORY_LABELS[rec.category] || "Reference")}
        </button>
        {origin === "seed" && (
          <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] px-2 py-1 rounded"
                style={{ background: "rgba(245,180,0,0.12)", color: "var(--rs-amber)" }}>
            Seed · read-only
          </span>
        )}
      </header>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--rs-amber)]">
          Kill Sheet v2 · Reference Libraries
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl tracking-tight mt-1">
          {isNew ? `New ${CATEGORY_LABELS[rec.category]} Record` : _recordTitle(rec)}
        </h1>
        {readOnly && (
          <p className="text-[11.5px] text-[var(--rs-text-2)] mt-2 leading-snug">
            Seed records are read-only. To edit, click <em>Clone to user library</em>
            below; your version will override the seed in pickers.
          </p>
        )}
      </div>

      {errors.length > 0 && (
        <div data-testid="reflibs-errors"
             className="card-rs p-3"
             style={{ borderColor: "rgba(252,165,165,0.5)", background: "rgba(252,165,165,0.06)" }}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-3.5 h-3.5" style={{ color: "#FCA5A5" }} />
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "#FCA5A5" }}>
              Cannot save — please fix:
            </span>
          </div>
          <ul className="list-disc list-inside text-[11.5px] text-[var(--rs-text-1)]">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* Category-specific form body */}
      {rec.category === "bop"  && <BopForm rec={rec} set={set} readOnly={readOnly} />}
      {rec.category === "bit"  && <BitForm rec={rec} set={set} readOnly={readOnly} />}
      {rec.category === "pump" && <PumpForm rec={rec} set={set} readOnly={readOnly} />}
      {rec.category === "mud_motor"        && <MudMotorForm   rec={rec} set={set} readOnly={readOnly} />}
      {rec.category === "rotary_steerable" && <RSSForm        rec={rec} set={set} readOnly={readOnly} />}
      {rec.category === "mwd_tool"         && <MWDForm        rec={rec} set={set} readOnly={readOnly} />}
      {rec.category === "lwd_tool"         && <LWDForm        rec={rec} set={set} readOnly={readOnly} />}
      {rec.category === "stabilizer"       && <StabilizerForm rec={rec} set={set} readOnly={readOnly} />}
      {rec.category === "crossover"        && <CrossoverForm  rec={rec} set={set} readOnly={readOnly} />}
      {!["bop","bit","pump","mud_motor","rotary_steerable","mwd_tool","lwd_tool","stabilizer","crossover"].includes(rec.category) && (<>

      {/* --- Tubular sections (Identity / Geometry / Strength) --- */}

      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Identity</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Category (required)">
            <NativeSelect value={rec.category}
                          onChange={(v) => set({ category: v })}
                          options={CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))}
                          testId="rec-category"
                          disabled={readOnly || !isNew} />
          </Field>
          <Field label="Nominal Size (required)" helper='e.g. "5 in", "6-5/8 in"'>
            <TextInput value={rec.nominalSize} onChange={(v) => set({ nominalSize: v })}
                       disabled={readOnly} testId="rec-nominalSize" />
          </Field>
          <Field label="Weight (lb/ft, required)">
            <NumIn value={rec.weight} onChange={(v) => set({ weight: v })}
                   disabled={readOnly} testId="rec-weight" />
          </Field>
          <Field label="Grade (required)" helper="e.g. E, X-95, G-105, S-135, J-55, N-80, P-110, Q-125, 100 ksi">
            <TextInput value={rec.grade} onChange={(v) => set({ grade: v })}
                       disabled={readOnly} testId="rec-grade" />
          </Field>
          <Field label="Connection (required)" helper="e.g. NC50, BTC, LTC, STC, EUE, NUE, Premium VAM TOP">
            <TextInput value={rec.connection} onChange={(v) => set({ connection: v })}
                       disabled={readOnly} testId="rec-connection" />
          </Field>
          <Field label="Thread Form">
            <TextInput value={rec.threadForm} onChange={(v) => set({ threadForm: v })}
                       disabled={readOnly} testId="rec-threadForm" />
          </Field>
        </div>
      </section>

      {/* Geometry */}
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Geometry</h2>
        <p className="text-[10.5px] text-[var(--rs-text-3)] mb-3 leading-snug">
          Diameters in inches. Internal capacity and external displacement are
          derived automatically (bbl/ft).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Outer Diameter (in, required)">
            <NumIn value={rec.outerDiameter} onChange={(v) => set({ outerDiameter: v })}
                   disabled={readOnly} testId="rec-outerDiameter" />
          </Field>
          <Field label="Inner Diameter (in, required)">
            <NumIn value={rec.innerDiameter} onChange={(v) => set({ innerDiameter: v })}
                   disabled={readOnly} testId="rec-innerDiameter" />
          </Field>
          <Field label="Drift (in, optional)">
            <NumIn value={rec.drift} onChange={(v) => set({ drift: v })}
                   disabled={readOnly} testId="rec-drift" />
          </Field>
          <Field label="Tool Joint OD (in, optional)">
            <NumIn value={rec.toolJointOd} onChange={(v) => set({ toolJointOd: v })}
                   disabled={readOnly} testId="rec-toolJointOd" />
          </Field>
          <Field label="Tool Joint ID (in, optional)">
            <NumIn value={rec.toolJointId} onChange={(v) => set({ toolJointId: v })}
                   disabled={readOnly} testId="rec-toolJointId" />
          </Field>
          <Field label="Coupling OD (in, optional)">
            <NumIn value={rec.couplingOd} onChange={(v) => set({ couplingOd: v })}
                   disabled={readOnly} testId="rec-couplingOd" />
          </Field>
        </div>

        {/* Derived */}
        <div className="grid grid-cols-2 gap-2 mt-3 p-2 rounded"
             data-testid="rec-derived"
             style={{ background: "rgba(245,180,0,0.06)", border: "1px dashed rgba(245,180,0,0.4)" }}>
          <div>
            <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--rs-text-3)]">
              Internal capacity
            </div>
            <div className="text-[13px] font-mono font-semibold text-[var(--rs-text-1)]">
              {rec.internalCapacity != null ? rec.internalCapacity.toFixed(5) : "—"}
              <span className="text-[10px] font-normal text-[var(--rs-text-3)] ml-1">bbl/ft</span>
            </div>
          </div>
          <div>
            <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--rs-text-3)]">
              External displacement
            </div>
            <div className="text-[13px] font-mono font-semibold text-[var(--rs-text-1)]">
              {rec.externalDisplacement != null ? rec.externalDisplacement.toFixed(5) : "—"}
              <span className="text-[10px] font-normal text-[var(--rs-text-3)] ml-1">bbl/ft</span>
            </div>
          </div>
        </div>
      </section>

      {/* Strength — reference only */}
      <section className="card-rs p-4">
        <header className="flex items-baseline justify-between gap-2 mb-3 flex-wrap">
          <h2 className="text-[13px] font-semibold">Strength (reference only)</h2>
          <span className="text-[10px] text-[var(--rs-text-3)] italic">
            Stored for future torsion/buckling features. NOT used by kill-sheet math today.
          </span>
        </header>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Tensile Yield (lb)">
            <NumIn value={rec.tensileYieldLb} onChange={(v) => set({ tensileYieldLb: v })}
                   disabled={readOnly} testId="rec-tensileYieldLb" />
          </Field>
          <Field label="Torsional Yield (ft·lb)">
            <NumIn value={rec.torsionalYieldFtLb} onChange={(v) => set({ torsionalYieldFtLb: v })}
                   disabled={readOnly} testId="rec-torsionalYieldFtLb" />
          </Field>
          <Field label="Internal Yield Pressure (psi)">
            <NumIn value={rec.internalYieldPressurePsi}
                   onChange={(v) => set({ internalYieldPressurePsi: v })}
                   disabled={readOnly} testId="rec-internalYieldPressurePsi" />
          </Field>
          <Field label="Collapse Pressure (psi)">
            <NumIn value={rec.collapsePressurePsi}
                   onChange={(v) => set({ collapsePressurePsi: v })}
                   disabled={readOnly} testId="rec-collapsePressurePsi" />
          </Field>
          <Field label="Minimum Yield Strength (psi)">
            <NumIn value={rec.minYieldStrengthPsi}
                   onChange={(v) => set({ minYieldStrengthPsi: v })}
                   disabled={readOnly} testId="rec-minYieldStrengthPsi" />
          </Field>
        </div>
      </section>
      </>)}

      <section className="card-rs p-4">
        <Field label="Notes">
          <TextArea value={rec.notes} onChange={(v) => set({ notes: v })}
                    disabled={readOnly} rows={2} testId="rec-notes" />
        </Field>
      </section>

      {/* Action bar */}
      <div className="mt-2 flex items-center justify-end gap-2 card-rs px-3 py-2"
           style={{ background: "rgba(10,11,14,0.92)" }}>
        <button type="button"
                onClick={handleBack}
                data-testid="reflibs-back"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px]"
                style={{ background: "transparent", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)", minHeight: 40 }}>
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {readOnly ? (
          <button type="button"
                  data-testid="reflibs-clone-seed"
                  onClick={handleCloneSeed}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium"
                  style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 40 }}>
            <Library className="w-4 h-4" /> Clone to user library
          </button>
        ) : (
          <button type="button"
                  data-testid="reflibs-save"
                  onClick={handleSave}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium"
                  style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 40 }}>
            <Save className="w-4 h-4" /> {isNew ? "Create record" : "Save changes"}
          </button>
        )}
      </div>

      <UnsavedChangesDialog
        open={unsaved.dialogOpen}
        onSaveAndLeave={unsaved.handleSaveAndLeave}
        onDiscard={unsaved.handleDiscard}
        onCancel={unsaved.handleCancel}
      />
    </div>
  );
}

// =============================================================================
// Module 2.1 — BOP / Bit / Pump editor sections
// =============================================================================
function BopForm({ rec, set, readOnly }) {
  return (
    <>
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">BOP Stack</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Stack name (required)">
            <TextInput value={rec.stackName} onChange={(v) => set({ stackName: v })}
                       disabled={readOnly} testId="rec-stackName" />
          </Field>
          <Field label="Type (required)">
            <NativeSelect value={rec.bopType} onChange={(v) => set({ bopType: v })}
                          options={BOP_TYPES.map(t => ({ value: t, label: t === "surface" ? "Surface" : "Subsea" }))}
                          testId="rec-bopType" disabled={readOnly} />
          </Field>
          <Field label="Nominal size (required)" helper='e.g. "13-5/8 in", "18-3/4 in"'>
            <TextInput value={rec.nominalSize} onChange={(v) => set({ nominalSize: v })}
                       disabled={readOnly} testId="rec-nominalSize" />
          </Field>
          <Field label="Working pressure (psi, required)">
            <NumIn value={rec.workingPressure} onChange={(v) => set({ workingPressure: v })}
                   disabled={readOnly} testId="rec-workingPressure" />
          </Field>
          <Field label="Ram cavities (required)" helper="Total number of ram cavities in the stack">
            <NumIn value={rec.ramSlots} onChange={(v) => set({ ramSlots: v })}
                   disabled={readOnly} testId="rec-ramSlots" />
          </Field>
          <Field label="Annular count">
            <NumIn value={rec.annularCount} onChange={(v) => set({ annularCount: v })}
                   disabled={readOnly} testId="rec-annularCount" />
          </Field>
          <Field label="Choke line ID (in)">
            <NumIn value={rec.chokeLineId} onChange={(v) => set({ chokeLineId: v })}
                   disabled={readOnly} testId="rec-chokeLineId" />
          </Field>
          <Field label="Kill line ID (in)">
            <NumIn value={rec.killLineId} onChange={(v) => set({ killLineId: v })}
                   disabled={readOnly} testId="rec-killLineId" />
          </Field>
          <Field label="Accumulator volume (gal)">
            <NumIn value={rec.accumulatorGal} onChange={(v) => set({ accumulatorGal: v })}
                   disabled={readOnly} testId="rec-accumulatorGal" />
          </Field>
          <Field label="Accumulator precharge (psi)">
            <NumIn value={rec.accumulatorPrechargePsi} onChange={(v) => set({ accumulatorPrechargePsi: v })}
                   disabled={readOnly} testId="rec-accumulatorPrechargePsi" />
          </Field>
        </div>
      </section>
    </>
  );
}

function BitForm({ rec, set, readOnly }) {
  return (
    <section className="card-rs p-4">
      <h2 className="text-[13px] font-semibold mb-3">Bit</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Bit size (in, required)">
          <NumIn value={rec.bitSize} onChange={(v) => set({ bitSize: v })}
                 disabled={readOnly} testId="rec-bitSize" />
        </Field>
        <Field label="Bit type (required)">
          <NativeSelect value={rec.bitType} onChange={(v) => set({ bitType: v })}
                        options={BIT_TYPES.map(t => ({ value: t, label: t }))}
                        testId="rec-bitType" disabled={readOnly} />
        </Field>
        <Field label="IADC code" helper="e.g. M323 (roller cone) or M433 (PDC)">
          <TextInput value={rec.iadcCode} onChange={(v) => set({ iadcCode: v })}
                     disabled={readOnly} testId="rec-iadcCode" />
        </Field>
        <Field label="Manufacturer">
          <TextInput value={rec.manufacturer} onChange={(v) => set({ manufacturer: v })}
                     disabled={readOnly} testId="rec-bitMfr" />
        </Field>
        <Field label="Model">
          <TextInput value={rec.model} onChange={(v) => set({ model: v })}
                     disabled={readOnly} testId="rec-bitModel" />
        </Field>
        <Field label="Nozzle count">
          <NumIn value={rec.nozzles} onChange={(v) => set({ nozzles: v })}
                 disabled={readOnly} testId="rec-nozzles" />
        </Field>
        <Field label="TFA (in²)" helper="Total flow area">
          <NumIn value={rec.tfa} onChange={(v) => set({ tfa: v })}
                 disabled={readOnly} testId="rec-tfa" />
        </Field>
      </div>
    </section>
  );
}

function PumpForm({ rec, set, readOnly }) {
  return (
    <section className="card-rs p-4">
      <h2 className="text-[13px] font-semibold mb-3">Mud Pump</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Pump model (required)">
          <TextInput value={rec.pumpModel} onChange={(v) => set({ pumpModel: v })}
                     disabled={readOnly} testId="rec-pumpModel" />
        </Field>
        <Field label="Manufacturer">
          <TextInput value={rec.manufacturer} onChange={(v) => set({ manufacturer: v })}
                     disabled={readOnly} testId="rec-pumpMfr" />
        </Field>
        <Field label="Pump type (required)">
          <NativeSelect value={rec.pumpType} onChange={(v) => set({ pumpType: v })}
                        options={PUMP_TYPES.map(t => ({ value: t, label: t === "triplex" ? "Triplex" : "Duplex" }))}
                        testId="rec-pumpType" disabled={readOnly} />
        </Field>
        <Field label="Liner size (in, required)">
          <NumIn value={rec.linerSize} onChange={(v) => set({ linerSize: v })}
                 disabled={readOnly} testId="rec-pumpLiner" />
        </Field>
        <Field label="Stroke length (in, required)">
          <NumIn value={rec.strokeLength} onChange={(v) => set({ strokeLength: v })}
                 disabled={readOnly} testId="rec-pumpStroke" />
        </Field>
        {rec.pumpType === "duplex" && (
          <Field label="Rod diameter (in, required)">
            <NumIn value={rec.rodDiameter} onChange={(v) => set({ rodDiameter: v })}
                   disabled={readOnly} testId="rec-pumpRod" />
          </Field>
        )}
        <Field label="Efficiency % (required)">
          <NumIn value={rec.efficiency} onChange={(v) => set({ efficiency: v })}
                 disabled={readOnly} testId="rec-pumpEff" />
        </Field>
        <Field label="Max pressure (psi)">
          <NumIn value={rec.maxPressurePsi} onChange={(v) => set({ maxPressurePsi: v })}
                 disabled={readOnly} testId="rec-pumpMaxP" />
        </Field>
      </div>
    </section>
  );
}


// =============================================================================
// Module 2.2 — BHA tool editor sections
//   Mud Motor / RSS / MWD / LWD / Stabilizer / Crossover
// =============================================================================

function MudMotorForm({ rec, set, readOnly }) {
  return (
    <section className="card-rs p-4">
      <h2 className="text-[13px] font-semibold mb-3">Mud Motor</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Motor model (required)" helper='e.g. "PowerPak 6 3/4 7/8 lobe"'>
          <TextInput value={rec.model} onChange={(v) => set({ model: v })}
                     disabled={readOnly} testId="rec-motor-model" />
        </Field>
        <Field label="Manufacturer">
          <TextInput value={rec.manufacturer} onChange={(v) => set({ manufacturer: v })}
                     disabled={readOnly} testId="rec-motor-mfr" />
        </Field>
        <Field label="OD (in, required)">
          <NumIn value={rec.outerDiameter} onChange={(v) => set({ outerDiameter: v })}
                 disabled={readOnly} testId="rec-motor-od" />
        </Field>
        <Field label="Length (ft)">
          <NumIn value={rec.lengthFt} onChange={(v) => set({ lengthFt: v })}
                 disabled={readOnly} testId="rec-motor-length" />
        </Field>
        <Field label="Lobe config" helper="A/B format, e.g. 5/6, 7/8">
          <TextInput value={rec.lobes} onChange={(v) => set({ lobes: v })}
                     disabled={readOnly} testId="rec-motor-lobes" />
        </Field>
        <Field label="Stages">
          <NumIn value={rec.stages} onChange={(v) => set({ stages: v })}
                 disabled={readOnly} testId="rec-motor-stages" />
        </Field>
        <Field label="Bend angle (°)" helper="0 for straight; max 5°">
          <NumIn value={rec.bendDegrees} onChange={(v) => set({ bendDegrees: v })}
                 disabled={readOnly} testId="rec-motor-bend" />
        </Field>
        <Field label="Max flow (gpm)">
          <NumIn value={rec.maxFlowGpm} onChange={(v) => set({ maxFlowGpm: v })}
                 disabled={readOnly} testId="rec-motor-flow" />
        </Field>
        <Field label="Max torque (ft·lb)">
          <NumIn value={rec.maxTorqueFtLb} onChange={(v) => set({ maxTorqueFtLb: v })}
                 disabled={readOnly} testId="rec-motor-torque" />
        </Field>
        <Field label="Max RPM">
          <NumIn value={rec.maxRpm} onChange={(v) => set({ maxRpm: v })}
                 disabled={readOnly} testId="rec-motor-rpm" />
        </Field>
        <Field label="Differential pressure (psi)">
          <NumIn value={rec.differentialPressurePsi}
                 onChange={(v) => set({ differentialPressurePsi: v })}
                 disabled={readOnly} testId="rec-motor-dp" />
        </Field>
        <Field label="Connection">
          <TextInput value={rec.connection} onChange={(v) => set({ connection: v })}
                     disabled={readOnly} testId="rec-motor-conn" />
        </Field>
      </div>
    </section>
  );
}

function RSSForm({ rec, set, readOnly }) {
  return (
    <section className="card-rs p-4">
      <h2 className="text-[13px] font-semibold mb-3">Rotary Steerable (RSS)</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="RSS model (required)">
          <TextInput value={rec.model} onChange={(v) => set({ model: v })}
                     disabled={readOnly} testId="rec-rss-model" />
        </Field>
        <Field label="Manufacturer">
          <TextInput value={rec.manufacturer} onChange={(v) => set({ manufacturer: v })}
                     disabled={readOnly} testId="rec-rss-mfr" />
        </Field>
        <Field label="OD (in, required)">
          <NumIn value={rec.outerDiameter} onChange={(v) => set({ outerDiameter: v })}
                 disabled={readOnly} testId="rec-rss-od" />
        </Field>
        <Field label="Length (ft)">
          <NumIn value={rec.lengthFt} onChange={(v) => set({ lengthFt: v })}
                 disabled={readOnly} testId="rec-rss-length" />
        </Field>
        <Field label="Steering mode (required)">
          <NativeSelect value={rec.steeringMode} onChange={(v) => set({ steeringMode: v })}
                        options={RSS_STEERING_MODES.map(m => ({ value: m, label: m }))}
                        testId="rec-rss-mode" disabled={readOnly} />
        </Field>
        <Field label="Max DLS (°/100 ft)">
          <NumIn value={rec.maxDoglegDeg100ft} onChange={(v) => set({ maxDoglegDeg100ft: v })}
                 disabled={readOnly} testId="rec-rss-dls" />
        </Field>
        <Field label="Max flow (gpm)">
          <NumIn value={rec.maxFlowGpm} onChange={(v) => set({ maxFlowGpm: v })}
                 disabled={readOnly} testId="rec-rss-flow" />
        </Field>
        <Field label="Max RPM">
          <NumIn value={rec.maxRpm} onChange={(v) => set({ maxRpm: v })}
                 disabled={readOnly} testId="rec-rss-rpm" />
        </Field>
        <Field label="Connection">
          <TextInput value={rec.connection} onChange={(v) => set({ connection: v })}
                     disabled={readOnly} testId="rec-rss-conn" />
        </Field>
      </div>
    </section>
  );
}

function MWDForm({ rec, set, readOnly }) {
  return (
    <section className="card-rs p-4">
      <h2 className="text-[13px] font-semibold mb-3">MWD Tool</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="MWD model (required)">
          <TextInput value={rec.model} onChange={(v) => set({ model: v })}
                     disabled={readOnly} testId="rec-mwd-model" />
        </Field>
        <Field label="Manufacturer">
          <TextInput value={rec.manufacturer} onChange={(v) => set({ manufacturer: v })}
                     disabled={readOnly} testId="rec-mwd-mfr" />
        </Field>
        <Field label="OD (in, required)">
          <NumIn value={rec.outerDiameter} onChange={(v) => set({ outerDiameter: v })}
                 disabled={readOnly} testId="rec-mwd-od" />
        </Field>
        <Field label="ID (in)">
          <NumIn value={rec.innerDiameter} onChange={(v) => set({ innerDiameter: v })}
                 disabled={readOnly} testId="rec-mwd-id" />
        </Field>
        <Field label="Length (ft)">
          <NumIn value={rec.lengthFt} onChange={(v) => set({ lengthFt: v })}
                 disabled={readOnly} testId="rec-mwd-length" />
        </Field>
        <Field label="Telemetry (required)">
          <NativeSelect value={rec.telemetryType} onChange={(v) => set({ telemetryType: v })}
                        options={MWD_TELEMETRY_TYPES.map(t => ({ value: t, label: t }))}
                        testId="rec-mwd-telemetry" disabled={readOnly} />
        </Field>
        <Field label="Max flow (gpm)">
          <NumIn value={rec.maxFlowGpm} onChange={(v) => set({ maxFlowGpm: v })}
                 disabled={readOnly} testId="rec-mwd-flow" />
        </Field>
        <Field label="Max pressure (psi)">
          <NumIn value={rec.maxPressurePsi} onChange={(v) => set({ maxPressurePsi: v })}
                 disabled={readOnly} testId="rec-mwd-pmax" />
        </Field>
        <Field label="Connection">
          <TextInput value={rec.connection} onChange={(v) => set({ connection: v })}
                     disabled={readOnly} testId="rec-mwd-conn" />
        </Field>
      </div>
    </section>
  );
}

function LWDForm({ rec, set, readOnly }) {
  return (
    <section className="card-rs p-4">
      <h2 className="text-[13px] font-semibold mb-3">LWD Tool</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="LWD model (required)">
          <TextInput value={rec.model} onChange={(v) => set({ model: v })}
                     disabled={readOnly} testId="rec-lwd-model" />
        </Field>
        <Field label="Manufacturer">
          <TextInput value={rec.manufacturer} onChange={(v) => set({ manufacturer: v })}
                     disabled={readOnly} testId="rec-lwd-mfr" />
        </Field>
        <Field label="OD (in, required)">
          <NumIn value={rec.outerDiameter} onChange={(v) => set({ outerDiameter: v })}
                 disabled={readOnly} testId="rec-lwd-od" />
        </Field>
        <Field label="ID (in)">
          <NumIn value={rec.innerDiameter} onChange={(v) => set({ innerDiameter: v })}
                 disabled={readOnly} testId="rec-lwd-id" />
        </Field>
        <Field label="Length (ft)">
          <NumIn value={rec.lengthFt} onChange={(v) => set({ lengthFt: v })}
                 disabled={readOnly} testId="rec-lwd-length" />
        </Field>
        <Field label="Sensor package" helper="e.g. gamma, resistivity, density">
          <TextInput value={rec.sensors} onChange={(v) => set({ sensors: v })}
                     disabled={readOnly} testId="rec-lwd-sensors" />
        </Field>
        <Field label="Max flow (gpm)">
          <NumIn value={rec.maxFlowGpm} onChange={(v) => set({ maxFlowGpm: v })}
                 disabled={readOnly} testId="rec-lwd-flow" />
        </Field>
        <Field label="Max pressure (psi)">
          <NumIn value={rec.maxPressurePsi} onChange={(v) => set({ maxPressurePsi: v })}
                 disabled={readOnly} testId="rec-lwd-pmax" />
        </Field>
        <Field label="Connection">
          <TextInput value={rec.connection} onChange={(v) => set({ connection: v })}
                     disabled={readOnly} testId="rec-lwd-conn" />
        </Field>
      </div>
    </section>
  );
}

function StabilizerForm({ rec, set, readOnly }) {
  return (
    <section className="card-rs p-4">
      <h2 className="text-[13px] font-semibold mb-3">Stabilizer</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Name / ID (required)">
          <TextInput value={rec.model} onChange={(v) => set({ model: v })}
                     disabled={readOnly} testId="rec-stab-model" />
        </Field>
        <Field label="Manufacturer">
          <TextInput value={rec.manufacturer} onChange={(v) => set({ manufacturer: v })}
                     disabled={readOnly} testId="rec-stab-mfr" />
        </Field>
        <Field label="OD (gauge, in, required)">
          <NumIn value={rec.outerDiameter} onChange={(v) => set({ outerDiameter: v })}
                 disabled={readOnly} testId="rec-stab-od" />
        </Field>
        <Field label="Length (ft)">
          <NumIn value={rec.lengthFt} onChange={(v) => set({ lengthFt: v })}
                 disabled={readOnly} testId="rec-stab-length" />
        </Field>
        <Field label="Blade type (required)">
          <NativeSelect value={rec.bladeType} onChange={(v) => set({ bladeType: v })}
                        options={STABILIZER_BLADE_TYPES.map(t => ({ value: t, label: t }))}
                        testId="rec-stab-bladeType" disabled={readOnly} />
        </Field>
        <Field label="Number of blades">
          <NumIn value={rec.blades} onChange={(v) => set({ blades: v })}
                 disabled={readOnly} testId="rec-stab-blades" />
        </Field>
        <Field label="Through-pipe ID (in)">
          <NumIn value={rec.throughPipeId} onChange={(v) => set({ throughPipeId: v })}
                 disabled={readOnly} testId="rec-stab-thruId" />
        </Field>
        <Field label="Connection">
          <TextInput value={rec.connection} onChange={(v) => set({ connection: v })}
                     disabled={readOnly} testId="rec-stab-conn" />
        </Field>
      </div>
    </section>
  );
}

function CrossoverForm({ rec, set, readOnly }) {
  const stepped = rec.stepped === true;
  return (
    <section className="card-rs p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[13px] font-semibold">Crossover Sub</h2>
        <label className="flex items-center gap-2 text-[11px] cursor-pointer select-none"
               data-testid="rec-xo-stepped-label">
          <input
            type="checkbox"
            checked={stepped}
            onChange={(e) => set({ stepped: e.target.checked })}
            disabled={readOnly}
            data-testid="rec-xo-stepped"
            className="accent-amber-500"
          />
          Stepped crossover
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Name / ID (required)" helper='e.g. "XO 4-1/2 IF Box × 4-1/2 REG Pin"'>
          <TextInput value={rec.model} onChange={(v) => set({ model: v })}
                     disabled={readOnly} testId="rec-xo-model" />
        </Field>
        <Field label="Top connection (required)">
          <TextInput value={rec.topConnection} onChange={(v) => set({ topConnection: v })}
                     disabled={readOnly} testId="rec-xo-top" />
        </Field>
        <Field label="Bottom connection (required)">
          <TextInput value={rec.bottomConnection} onChange={(v) => set({ bottomConnection: v })}
                     disabled={readOnly} testId="rec-xo-bot" />
        </Field>

        {stepped ? (
          <>
            <Field label="OD 1 (in)" helper="Top-side OD section">
              <NumIn value={rec.od1} onChange={(v) => set({ od1: v })}
                     disabled={readOnly} testId="rec-xo-od1" />
            </Field>
            <Field label="Length 1 (in)">
              <NumIn value={rec.len1} onChange={(v) => set({ len1: v })}
                     disabled={readOnly} testId="rec-xo-len1" />
            </Field>
            <Field label="OD 2 (in)" helper="Bottom-side OD section">
              <NumIn value={rec.od2} onChange={(v) => set({ od2: v })}
                     disabled={readOnly} testId="rec-xo-od2" />
            </Field>
            <Field label="Length 2 (in)">
              <NumIn value={rec.len2} onChange={(v) => set({ len2: v })}
                     disabled={readOnly} testId="rec-xo-len2" />
            </Field>
          </>
        ) : (
          <Field label="OD (in)">
            <NumIn value={rec.outerDiameter} onChange={(v) => set({ outerDiameter: v })}
                   disabled={readOnly} testId="rec-xo-od" />
          </Field>
        )}

        <Field label="ID / bore (in)">
          <NumIn value={rec.innerDiameter} onChange={(v) => set({ innerDiameter: v })}
                 disabled={readOnly} testId="rec-xo-id" />
        </Field>
        <Field label={stepped ? "Overall length (in)" : "Length (in)"}
               helper={stepped ? "Must be ≥ Length 1 + Length 2" : undefined}>
          <NumIn value={rec.lengthIn} onChange={(v) => set({ lengthIn: v })}
                 disabled={readOnly} testId="rec-xo-length" />
        </Field>
      </div>
    </section>
  );
}
